import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Archive, ArrowLeft, Pencil, Plus, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { canArchiveCRM, canCreateOrEditCRM, canDeleteActivity, canRestoreCRM } from "../lib/crm-permissions";
import { ASSIGNEE_UNASSIGNED, getAssigneeLabel, getTaskAssigneeLabel, toAssignedUserId } from "../lib/tasks-assignees";
import { isTaskOverdue } from "../lib/task-detail";
import PageHeader from "../components/layout/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import StatusPill from "../components/status/StatusPill";
import Timeline from "../components/activity/Timeline";
import AddActivityDialog from "../components/activity/AddActivityDialog";
import QuickNoteDialog from "../components/activity/QuickNoteDialog";
import DealFormDialog, { buildConversionInitialValuesFromLead } from "../components/deals/DealFormDialog";
import { LEAD_STATUS, LEAD_SOURCE, LEAD_INTEREST, PRIORITY, TASK_STATUS } from "../lib/enums";
import {
  LEAD_STATUS_LABELS,
  LEAD_SOURCE_LABELS,
  LEAD_INTEREST_LABELS,
  PRIORITY_LABELS,
  TASK_STATUS_LABELS,
} from "../lib/labels";
import { formatDate, formatDateTime } from "../lib/format";
import {
  canConvertLead,
  getLeadConversionDateLabel,
  isLeadConverted,
} from "../lib/lead-conversion-state";
import { getLeadStatusOptionsForCurrentStatus } from "../lib/lead-status-options";
import {
  buildCompanyPayloadFromLead,
  buildContactPayloadFromLead,
  canCreateCompanyFromLead,
  canCreateContactFromLead,
} from "../lib/lead-conversion";
import {
  DETAIL_ACTIVITY_PAGE_SIZE,
  mergeActivities,
  normalizeActivityPageResponse,
} from "../lib/activity-pagination";
import { isPublicDemoLead } from "./publicLeadVisibility";
import { buildLeadPublicSubmissionCardData } from "./leadPublicSubmissionContent";

function normalizeEmail(value) {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return "";
  const lower = raw.toLowerCase();
  const noMailto = lower.startsWith("mailto:") ? lower.slice("mailto:".length).trim() : lower;
  const angle = noMailto.match(/<([^>]+)>/);
  return (angle ? angle[1] : noMailto).trim();
}

const TASK_FORM_INITIAL = {
  title: "",
  description: "",
  due_date: "",
  priority: PRIORITY.MEDIUM,
  status: TASK_STATUS.OPEN,
  assigned_user_id: ASSIGNEE_UNASSIGNED,
};

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useAuth();
  const perm = { role, isSuperAdmin };
  const canDeleteActivities = canDeleteActivity(perm);
  const [lead, setLead] = useState(null);
  const [company, setCompany] = useState(null);
  const [contact, setContact] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activitiesHasMore, setActivitiesHasMore] = useState(false);
  const [activitiesCursor, setActivitiesCursor] = useState(null);
  const [activitiesLoadingMore, setActivitiesLoadingMore] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneesError, setAssigneesError] = useState("");
  const [publicSubmission, setPublicSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(TASK_FORM_INITIAL);
  const [taskSaving, setTaskSaving] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [quickNoteDialogOpen, setQuickNoteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);
  const [linkingCompany, setLinkingCompany] = useState(false);
  const [matchingCompanies, setMatchingCompanies] = useState([]);
  const [matchingContacts, setMatchingContacts] = useState([]);

  const assigneesById = useMemo(() => {
    const map = {};
    for (const u of assignees || []) {
      if (u?.id) map[u.id] = u;
    }
    return map;
  }, [assignees]);

  const assigneeOptions = useMemo(() => {
    const items = (assignees || [])
      .filter((u) => u?.id)
      .map((u) => ({ id: u.id, label: getAssigneeLabel(u) }));
    items.sort((a, b) => a.label.localeCompare(b.label, "de"));
    return items;
  }, [assignees]);

  const loadAssignees = useCallback(async () => {
    setAssigneesLoading(true);
    setAssigneesError("");
    try {
      const r = await api.get("/api/settings/users");
      setAssignees(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setAssignees([]);
      setAssigneesError(extractApiError(e, "Teammitglieder konnten nicht geladen werden."));
    } finally {
      setAssigneesLoading(false);
    }
  }, []);

  const loadActivities = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setActivitiesLoadingMore(true);
      try {
        const params = { lead_id: id, limit: DETAIL_ACTIVITY_PAGE_SIZE };
        if (cursor?.created_at && cursor?.id) {
          params.before_created_at = cursor.created_at;
          params.before_id = cursor.id;
        }
        const r = await api.get("/api/activities/page", { params });
        const page = normalizeActivityPageResponse(r.data);
        setActivities((prev) => (append ? mergeActivities(prev, page.items) : page.items));
        setActivitiesHasMore(page.hasMore);
        setActivitiesCursor(page.nextCursor);
      } catch (e) {
        toast.error(extractApiError(e, "Aktivitäten konnten nicht geladen werden."));
        if (!append) {
          setActivities([]);
          setActivitiesHasMore(false);
          setActivitiesCursor(null);
        }
      } finally {
        if (append) setActivitiesLoadingMore(false);
      }
    },
    [id]
  );

  const handleDeleteActivity = useCallback(async (activity) => {
    try {
      await api.delete(`/api/activities/${activity.id}`);
      setActivities((prev) => prev.filter((item) => item.id !== activity.id));
      toast.success("Aktivität gelöscht.");
    } catch (e) {
      const msg = extractApiError(e, "Aktivität konnte nicht gelöscht werden.");
      toast.error(msg);
      throw e;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [leadR, taskR, _activities, submissionData] = await Promise.all([
        api.get(`/api/leads/${id}`),
        api.get(`/api/tasks`, { params: { related_lead_id: id } }),
        loadActivities(),
        api
          .get(`/api/leads/${id}/public-form-submission`)
          .then((r) => r.data)
          .catch((e) => {
            if (e?.response?.status === 404) return null;
            throw e;
          }),
      ]);
      const leadData = leadR.data;
      setLead(leadData);
      setPublicSubmission(submissionData);
      const [companyR, contactR] = await Promise.all([
        leadData.company_id ? api.get(`/api/companies/${leadData.company_id}`).then((r) => r.data).catch(() => null) : Promise.resolve(null),
        leadData.contact_id ? api.get(`/api/contacts/${leadData.contact_id}`).then((r) => r.data).catch(() => null) : Promise.resolve(null),
      ]);
      setCompany(companyR);
      setContact(contactR);
      setTasks(Array.isArray(taskR.data) ? taskR.data : []);
    } catch (e) {
      setPublicSubmission(null);
      toast.error(extractApiError(e, "Lead konnte nicht geladen werden."));
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  }, [id, loadActivities, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadAssignees();
  }, [loadAssignees]);

  useEffect(() => {
    let cancelled = false;

    async function loadDuplicateHints() {
      if (!lead) {
        setMatchingCompanies([]);
        setMatchingContacts([]);
        return;
      }

      if (canCreateCompanyFromLead(lead)) {
        try {
          const response = await api.get("/api/companies", {
            params: { search: lead.company_name.trim() },
          });
          if (!cancelled) {
            setMatchingCompanies(Array.isArray(response.data) ? response.data.slice(0, 3) : []);
          }
        } catch {
          if (!cancelled) setMatchingCompanies([]);
        }
      } else {
        setMatchingCompanies([]);
      }

      if (canCreateContactFromLead(lead) && (lead.contact_email || "").trim()) {
        const normalizedEmail = normalizeEmail(lead.contact_email);
        if (!normalizedEmail) {
          setMatchingContacts([]);
          return;
        }
        try {
          const response = await api.get("/api/contacts", {
            params: { search: normalizedEmail },
          });
          if (!cancelled) {
            const items = Array.isArray(response.data) ? response.data : [];
            const matches = items.filter((c) => normalizeEmail(c?.email) === normalizedEmail);
            setMatchingContacts(matches.slice(0, 3));
          }
        } catch {
          if (!cancelled) setMatchingContacts([]);
        }
      } else {
        setMatchingContacts([]);
      }
    }

    loadDuplicateHints();

    return () => {
      cancelled = true;
    };
  }, [lead]);

  const handleField = (field, value) => setLead((l) => ({ ...l, [field]: value }));

  const isArchived = Boolean(lead?.archived_at);
  const isConverted = isLeadConverted(lead);
  const conversionDateLabel = getLeadConversionDateLabel(lead?.converted_at);
  const canEdit = canCreateOrEditCRM(perm) && !isArchived;
  const canConvert = canConvertLead({ perm, lead });
  const canArchive = canArchiveCRM(perm) && !isArchived;
  const canRestore = canRestoreCRM(perm) && isArchived;

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    if (!taskForm.title.trim()) return toast.error("Bitte einen Titel angeben.");
    setTaskSaving(true);
    try {
      const assignedUserId = toAssignedUserId(taskForm.assigned_user_id);
      const payload = {
        title: taskForm.title,
        description: taskForm.description,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null,
        priority: taskForm.priority,
        status: taskForm.status,
        related_lead_id: id,
        ...(assignedUserId ? { assigned_user_id: assignedUserId } : {}),
      };
      await api.post("/api/tasks", payload);
      toast.success("Aufgabe erstellt.");
      setTaskForm(TASK_FORM_INITIAL);
      setTaskDialogOpen(false);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Aufgabe konnte nicht erstellt werden."));
    } finally {
      setTaskSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = {
        title: lead.title,
        contact_name: lead.contact_name,
        contact_email: lead.contact_email,
        contact_phone: lead.contact_phone,
        company_name: lead.company_name,
        description: lead.description,
        source: lead.source,
        interest: lead.interest,
        status: lead.status,
        priority: lead.priority,
      };
      await api.patch(`/api/leads/${id}`, payload);
      toast.success("Lead gespeichert.");
      load();
    } catch (e) {
      toast.error(extractApiError(e, "Speichern fehlgeschlagen."));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!canArchive) return;
    setArchiving(true);
    try {
      await api.post(`/api/leads/${id}/archive`);
      toast.success("Lead wurde archiviert.");
      setArchiveOpen(false);
      load();
    } catch (e) {
      toast.error(extractApiError(e, "Archivieren fehlgeschlagen."));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!canRestore) return;
    setRestoring(true);
    try {
      await api.post(`/api/leads/${id}/restore`);
      toast.success("Lead wurde wiederhergestellt.");
      load();
    } catch (e) {
      toast.error(extractApiError(e, "Wiederherstellen fehlgeschlagen."));
    } finally {
      setRestoring(false);
    }
  };

  const handleCreateCompanyFromLead = async () => {
    if (!canEdit || !canCreateCompanyFromLead(lead)) return;
    setCreatingCompany(true);
    try {
      const companyResponse = await api.post("/api/companies", buildCompanyPayloadFromLead(lead));
      const createdCompanyId = companyResponse?.data?.id;
      if (!createdCompanyId) throw new Error("Firma konnte nicht erstellt werden.");
      await api.patch(`/api/leads/${id}`, { company_id: createdCompanyId });
      toast.success("Firma erstellt und mit dem Lead verknüpft.");
      await load();
    } catch (e) {
      toast.error(extractApiError(e, "Firma konnte nicht aus dem Lead erstellt werden."));
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleCreateContactFromLead = async () => {
    if (!canEdit || !canCreateContactFromLead(lead) || matchingContacts.length > 0) return;
    setCreatingContact(true);
    try {
      const contactResponse = await api.post(
        "/api/contacts",
        buildContactPayloadFromLead(lead, publicSubmission)
      );
      const createdContactId = contactResponse?.data?.id;
      if (!createdContactId) throw new Error("Kontakt konnte nicht erstellt werden.");
      await api.patch(`/api/leads/${id}`, { contact_id: createdContactId });
      toast.success("Kontakt erstellt und mit dem Lead verknüpft.");
      await load();
    } catch (e) {
      toast.error(extractApiError(e, "Kontakt konnte nicht aus dem Lead erstellt werden."));
    } finally {
      setCreatingContact(false);
    }
  };

  const handleLinkLeadToExistingCompany = async () => {
    if (!canEdit || !firstMatchingCompany?.id || lead.company_id) return;
    setLinkingCompany(true);
    try {
      await api.patch(`/api/leads/${id}`, { company_id: firstMatchingCompany.id });
      toast.success("Lead mit bestehender Firma verknüpft.");
      await load();
    } catch (e) {
      toast.error(extractApiError(e, "Lead konnte nicht mit der bestehenden Firma verknüpft werden."));
    } finally {
      setLinkingCompany(false);
    }
  };

  if (loading || !lead) {
    return <div className="text-sm text-muted-foreground py-12 text-center">Wird geladen …</div>;
  }

  const st = LEAD_STATUS_LABELS[lead.status] || { de: lead.status, tone: "muted" };
  const pr = PRIORITY_LABELS[lead.priority] || { de: lead.priority, tone: "muted" };
  const hasCompanyRelation = Boolean(lead.company_id);
  const hasContactRelation = Boolean(lead.contact_id);
  const hasAnyRelation = hasCompanyRelation || hasContactRelation;
  const companySnapshotLabel = hasCompanyRelation ? "Firma (Anfragedaten)" : "Firma";
  const contactNameSnapshotLabel = hasContactRelation ? "Kontaktname (Anfragedaten)" : "Kontaktname";
  const contactEmailSnapshotLabel = hasContactRelation ? "E-Mail (Anfragedaten)" : "E-Mail";
  const contactPhoneSnapshotLabel = hasContactRelation ? "Telefon (Anfragedaten)" : "Telefon";
  const showRequestContext = isPublicDemoLead(lead);
  const publicSubmissionCard = buildLeadPublicSubmissionCardData(publicSubmission);
  const showPublicSubmissionCard = Boolean(publicSubmissionCard);
  const requestPhone = (lead.contact_phone || "").trim();
  const requestMessage = (lead.description || "").trim();
  const canCreateCompanyAction = canEdit && canCreateCompanyFromLead(lead);
  const firstMatchingCompany = matchingCompanies[0] || null;
  const firstMatchingContact = matchingContacts[0] || null;
  const hasMatchingContactDuplicate = Boolean(firstMatchingContact);
  const canCreateContactBase = canEdit && canCreateContactFromLead(lead);
  const canCreateContactAction = canCreateContactBase && !hasMatchingContactDuplicate;
  const showContactDuplicateGuard = canCreateContactBase && hasMatchingContactDuplicate;

  return (
    <div>
      <button
        onClick={() => navigate("/leads")}
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center mb-3"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zu Leads
      </button>
      {isArchived ? (
        <Alert className="mb-4 border-border/60 bg-muted/20">
          <AlertTitle>Archiviert</AlertTitle>
          <AlertDescription>Dieser Lead ist archiviert.</AlertDescription>
        </Alert>
      ) : null}
      {isConverted ? (
        <Alert className="mb-4 border-border/60 bg-muted/20">
          <AlertTitle>In Deal umgewandelt</AlertTitle>
          <AlertDescription>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <div>Dieser Lead wurde in einen Deal umgewandelt.</div>
                <div className="text-xs text-muted-foreground">
                  Der erzeugte Deal ist jetzt der operative Vertriebsprozess.
                </div>
                {conversionDateLabel ? (
                  <div className="text-xs text-muted-foreground">{conversionDateLabel}</div>
                ) : null}
              </div>
              <Button
                variant="outline"
                onClick={() => navigate(`/deals/${lead.converted_deal_id}`)}
                data-testid="lead-open-converted-deal-button"
              >
                Deal öffnen
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <PageHeader
        title={lead.title}
        description={
          <span className="inline-flex items-center gap-2">
            <StatusPill tone={st.tone}>{st.de}</StatusPill>
            <StatusPill tone={pr.tone}>{pr.de}</StatusPill>
            <span className="text-xs text-muted-foreground">
              Erstellt am {formatDate(lead.created_at)}
            </span>
          </span>
        }
        actions={
          <>
            {canArchive ? (
              <Button variant="outline" onClick={() => setArchiveOpen(true)} data-testid="lead-archive-button">
                <Archive className="w-4 h-4 mr-1.5" /> Archivieren
              </Button>
            ) : null}
            {canRestore ? (
              <Button variant="outline" onClick={handleRestore} disabled={restoring} data-testid="lead-restore-button">
                <RotateCcw className="w-4 h-4 mr-1.5" /> Wiederherstellen
              </Button>
            ) : null}
            {canConvert ? (
              <Button
                variant="outline"
                onClick={() => setConvertDialogOpen(true)}
                data-testid="lead-convert-button"
              >
                In Deal umwandeln
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                onClick={handleSave}
                disabled={saving}
                className="text-white"
                style={{ background: "var(--brand)" }}
                data-testid="lead-save-button"
              >
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? "Speichern …" : "Speichern"}
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Verknüpfungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {hasAnyRelation ? (
                <>
                  {hasCompanyRelation ? (
                    <div className="flex items-center justify-between gap-4">
                      <div className="text-sm text-muted-foreground">Firma</div>
                      <button
                        className="text-sm font-medium hover:underline"
                        onClick={() => navigate(`/firmen/${lead.company_id}`)}
                      >
                        {company?.name || "Firma öffnen"}
                      </button>
                    </div>
                  ) : null}
                  {hasContactRelation ? (
                    <div className="flex items-center justify-between gap-4">
                      <div className="text-sm text-muted-foreground">Kontakt</div>
                      <button
                        className="text-sm font-medium hover:underline"
                        onClick={() => navigate(`/kontakte/${lead.contact_id}`)}
                      >
                        {contact ? `${contact.first_name} ${contact.last_name}` : "Kontakt öffnen"}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Dieser Lead enthält aktuell nur Anfragedaten (ohne verknüpften Kontakt oder Firma).
                </div>
              )}
              {canCreateCompanyAction || canCreateContactAction || showContactDuplicateGuard ? (
                <div className="border-t border-border/60 pt-3 space-y-3">
                  <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                    In CRM übernehmen
                  </div>
                  {canCreateCompanyAction ? (
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCreateCompanyFromLead}
                        disabled={creatingCompany}
                        data-testid="lead-create-company-button"
                      >
                        <Plus className="w-4 h-4 mr-1.5" />
                        {creatingCompany ? "Firma wird erstellt …" : "Firma erstellen"}
                      </Button>
                      {firstMatchingCompany ? (
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span>Möglicherweise existiert diese Firma bereits.</span>
                          <button
                            type="button"
                            className="font-medium hover:underline"
                            onClick={() => navigate(`/firmen/${firstMatchingCompany.id}`)}
                          >
                            {firstMatchingCompany.name}
                          </button>
                          {!hasCompanyRelation ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleLinkLeadToExistingCompany}
                              disabled={linkingCompany}
                              data-testid="lead-link-existing-company-button"
                            >
                              {linkingCompany ? "Lead wird verknüpft …" : "Lead mit Firma verknüpfen"}
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {canCreateContactBase ? (
                    <div className="space-y-2">
                      {firstMatchingContact ? (
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span>
                            Ein Kontakt mit dieser E-Mail existiert bereits. Bitte öffne den
                            bestehenden Kontakt oder prüfe die E-Mail-Adresse, bevor ein neuer
                            Kontakt angelegt wird.
                          </span>
                          <button
                            type="button"
                            className="font-medium hover:underline"
                            onClick={() => navigate(`/kontakte/${firstMatchingContact.id}`)}
                            data-testid="lead-open-matching-contact-button"
                          >
                            Bestehenden Kontakt öffnen
                          </button>
                        </div>
                      ) : null}
                      {canCreateContactAction ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCreateContactFromLead}
                          disabled={creatingContact}
                          data-testid="lead-create-contact-button"
                        >
                          <Plus className="w-4 h-4 mr-1.5" />
                          {creatingContact ? "Kontakt wird erstellt …" : "Kontakt erstellen"}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {showPublicSubmissionCard ? (
            <Card>
              <CardHeader>
                <CardTitle>Formularanfrage</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Quelle</div>
                  <div className="text-sm">{LEAD_SOURCE_LABELS[lead.source] || lead.source || "—"}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Eingegangen am</div>
                  <div className="text-sm">{formatDateTime(publicSubmissionCard.submittedAt)}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Datenschutz akzeptiert</div>
                  <div className="text-sm">{publicSubmissionCard.privacyAccepted ? "Ja" : "Nein"}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Interesse</div>
                  <div className="text-sm">
                    {LEAD_INTEREST_LABELS[publicSubmissionCard.fields.interest] ||
                      publicSubmissionCard.fields.interest ||
                      "—"}
                  </div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Kontaktname</div>
                  <div className="text-sm">{publicSubmissionCard.fields.name || "—"}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">E-Mail</div>
                  <div className="text-sm">{publicSubmissionCard.fields.email || "—"}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Firma</div>
                  <div className="text-sm">{publicSubmissionCard.fields.company || "—"}</div>
                </div>
                {publicSubmissionCard.fields.phone ? (
                  <div className="space-y-0.5">
                    <div className="text-xs text-muted-foreground">Telefon</div>
                    <div className="text-sm">{publicSubmissionCard.fields.phone}</div>
                  </div>
                ) : null}
                {publicSubmissionCard.fields.message ? (
                  <div className="md:col-span-2 space-y-0.5">
                    <div className="text-xs text-muted-foreground">Nachricht</div>
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap max-h-24 overflow-hidden">
                      {publicSubmissionCard.fields.message}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : showRequestContext ? (
            <Card>
              <CardHeader>
                <CardTitle>Öffentliche Anfrage</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Quelle</div>
                  <div className="text-sm">{LEAD_SOURCE_LABELS[lead.source] || lead.source || "—"}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Interesse</div>
                  <div className="text-sm">{LEAD_INTEREST_LABELS[lead.interest] || lead.interest || "—"}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Kontaktname</div>
                  <div className="text-sm">{lead.contact_name || "—"}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">E-Mail</div>
                  <div className="text-sm">{lead.contact_email || "—"}</div>
                </div>
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Firma</div>
                  <div className="text-sm">{lead.company_name || "—"}</div>
                </div>
                {requestPhone ? (
                  <div className="space-y-0.5">
                    <div className="text-xs text-muted-foreground">Telefon</div>
                    <div className="text-sm">{requestPhone}</div>
                  </div>
                ) : null}
                {requestMessage ? (
                  <div className="md:col-span-2 space-y-0.5">
                    <div className="text-xs text-muted-foreground">Nachricht</div>
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap max-h-24 overflow-hidden">
                      {requestMessage}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Stammdaten</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Titel"><Input value={lead.title || ""} onChange={(e) => handleField("title", e.target.value)} data-testid="lead-edit-title" disabled={!canEdit} /></Field>
              <Field label={contactNameSnapshotLabel}><Input value={lead.contact_name || ""} onChange={(e) => handleField("contact_name", e.target.value)} disabled={!canEdit} /></Field>
              <Field label={contactEmailSnapshotLabel}><Input value={lead.contact_email || ""} onChange={(e) => handleField("contact_email", e.target.value)} disabled={!canEdit} /></Field>
              <Field label={contactPhoneSnapshotLabel}><Input value={lead.contact_phone || ""} onChange={(e) => handleField("contact_phone", e.target.value)} disabled={!canEdit} /></Field>
              <Field label={companySnapshotLabel}><Input value={lead.company_name || ""} onChange={(e) => handleField("company_name", e.target.value)} disabled={!canEdit} /></Field>
              <Field label="Quelle">
                <Select value={lead.source} onValueChange={(v) => handleField("source", v)} disabled={!canEdit}>
                  <SelectTrigger data-testid="lead-edit-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.values(LEAD_SOURCE).map((s) => (
                      <SelectItem key={s} value={s}>{LEAD_SOURCE_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Interesse">
                <Select value={lead.interest} onValueChange={(v) => handleField("interest", v)} disabled={!canEdit}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.values(LEAD_INTEREST).map((s) => (
                      <SelectItem key={s} value={s}>{LEAD_INTEREST_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={lead.status} onValueChange={(v) => handleField("status", v)} disabled={!canEdit}>
                  <SelectTrigger data-testid="lead-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getLeadStatusOptionsForCurrentStatus(lead.status).map((o) => (
                      <SelectItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Priorität">
                <Select value={lead.priority} onValueChange={(v) => handleField("priority", v)} disabled={!canEdit}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.values(PRIORITY).map((s) => (
                      <SelectItem key={s} value={s}>{PRIORITY_LABELS[s].de}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Beschreibung / Nachricht">
                  <Textarea
                    rows={4}
                    value={lead.description || ""}
                    onChange={(e) => handleField("description", e.target.value)}
                    disabled={!canEdit}
                  />
                </Field>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-5 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Verknüpfte Aufgaben</CardTitle>
              {canEdit ? (
                <Button size="sm" variant="outline" onClick={() => setTaskDialogOpen(true)}>
                  <Plus className="w-4 h-4" /> Aufgabe hinzufügen
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <div className="text-sm text-muted-foreground">Keine Aufgaben verknüpft.</div>
              ) : (
                <ul className="divide-y divide-border/60">
                  {tasks.map((t) => {
                    const stt = TASK_STATUS_LABELS[t.status] || { de: t.status, tone: "muted" };
                    const prt = PRIORITY_LABELS[t.priority] || { de: t.priority, tone: "muted" };
                    const overdue = isTaskOverdue(t.due_date, t.status);
                    const assigneeLabel = t.assigned_user_id
                      ? getTaskAssigneeLabel({ assignedUserId: t.assigned_user_id, usersById: assigneesById })
                      : "";
                    return (
                      <li key={t.id} className="py-2 flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <button
                            type="button"
                            className="text-sm font-medium break-words hover:underline text-left"
                            onClick={() => navigate(`/aufgaben/${t.id}`)}
                          >
                            {t.title}
                          </button>
                          {t.due_date && (
                            <div className={`text-xs ${overdue ? "text-destructive" : "text-muted-foreground"}`}>
                              Fällig {formatDate(t.due_date)}
                            </div>
                          )}
                          {t.assigned_user_id ? (
                            <div className="text-xs text-muted-foreground">Zugewiesen an: {assigneeLabel}</div>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {overdue ? <StatusPill tone="destructive">Überfällig</StatusPill> : null}
                          <StatusPill tone={prt.tone}>{prt.de}</StatusPill>
                          <StatusPill tone={stt.tone}>{stt.de}</StatusPill>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Aktivitäten</CardTitle>
              {canEdit ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setQuickNoteDialogOpen(true)}>
                    <Pencil className="w-4 h-4 mr-2" /> Notiz
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActivityDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Aktivität
                  </Button>
                </div>
              ) : null}
            </CardHeader>
            <CardContent>
              <Timeline
                items={activities}
                usersById={assigneesById}
                currentContext={{ type: "lead", id: lead.id }}
                canDelete={canDeleteActivities}
                onDelete={handleDeleteActivity}
              />
              <div className="mt-4 flex flex-col items-center gap-2">
                {activitiesHasMore ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => loadActivities({ cursor: activitiesCursor, append: true })}
                    disabled={activitiesLoadingMore}
                  >
                    {activitiesLoadingMore ? "Weitere Aktivitäten werden geladen …" : "Mehr Aktivitäten laden"}
                  </Button>
                ) : activities.length > 0 ? (
                  <div className="text-xs text-muted-foreground">Alle Aktivitäten geladen</div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          if (taskSaving) return;
          setTaskDialogOpen(open);
          if (!open) setTaskForm(TASK_FORM_INITIAL);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Aufgabe hinzufügen</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-3" data-testid="lead-task-create-form">
            <Field label="Titel *">
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm((s) => ({ ...s, title: e.target.value }))}
                autoFocus
                data-testid="lead-task-form-title"
              />
            </Field>
            <Field label="Beschreibung">
              <Textarea
                rows={3}
                value={taskForm.description}
                onChange={(e) => setTaskForm((s) => ({ ...s, description: e.target.value }))}
              />
            </Field>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Fälligkeitsdatum">
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm((s) => ({ ...s, due_date: e.target.value }))}
                />
              </Field>
              <Field label="Zugewiesen an">
                <Select
                  value={taskForm.assigned_user_id}
                  onValueChange={(v) => setTaskForm((s) => ({ ...s, assigned_user_id: v }))}
                  disabled={assigneesLoading || Boolean(assigneesError)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ASSIGNEE_UNASSIGNED}>Nicht zugewiesen</SelectItem>
                    {assigneeOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {assigneesError ? <div className="text-xs text-muted-foreground mt-1">{assigneesError}</div> : null}
              </Field>
              <Field label="Priorität">
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm((s) => ({ ...s, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(PRIORITY).map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p].de}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={taskForm.status} onValueChange={(v) => setTaskForm((s) => ({ ...s, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(TASK_STATUS).map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s].de}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)} disabled={taskSaving}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={taskSaving}>
                {taskSaving ? "Speichern …" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <QuickNoteDialog
        open={quickNoteDialogOpen}
        onOpenChange={setQuickNoteDialogOpen}
        relationPayload={{ related_lead_id: id }}
        onCreated={() => loadActivities()}
      />
      <AddActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        relationPayload={{ related_lead_id: id }}
        onCreated={() => loadActivities()}
      />

      <DealFormDialog
        open={convertDialogOpen}
        onOpenChange={setConvertDialogOpen}
        mode="convert"
        convertLeadId={lead.id}
        initialValues={buildConversionInitialValuesFromLead(lead)}
        onSaved={(deal) => {
          setConvertDialogOpen(false);
          navigate(`/deals/${deal.id}`);
        }}
      />

      <AlertDialog
        open={archiveOpen}
        onOpenChange={(open) => {
          if (archiving) return;
          setArchiveOpen(open);
        }}
      >
        <AlertDialogContent className="border-border/60 shadow-lg shadow-black/10 outline-none ring-0 ring-offset-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Lead archivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Lead bleibt im System erhalten, wird aber standardmäßig nicht mehr in Listen angezeigt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiving}
              onClick={(event) => {
                event.preventDefault();
                handleArchive();
              }}
            >
              {archiving ? "Archivieren …" : "Archivieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
